import React, { useState, useEffect, useRef } from 'react';
import { Button } from './button';
import { Text } from './text';
import { Heading } from './heading';
import { Dialog, DialogBody, DialogTitle, DialogActions } from './dialog';
import { NotifyFianzaDialog } from './NotifyFianzaDialog';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { db } from '../firebase';
import FileReceiver from './FileReceiver';
import { CheckCircleIcon, XCircleIcon, ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useUser } from './UserContext';
import { logBitacora } from '../utils/bitacora';
import { mutateContract } from '../utils/contractWrites';
import { parseXmlFianza, parseXmlFactura, validateFianzaFactura } from '../utils/xmlParsers';
import { getProveedorMismatch, namesMatch } from '../utils/convenioCompliance';
import { rfcsMatch } from '../utils/identityMismatch';
import { createEmisionEvento, createEmisionFactura, ESTADO_FIANZA } from '../utils/fianzaEventos';
import {
  calculateTarifa,
  calculateComision,
  getPrimaAmount,
  getEmisorNombre,
  getEmisorRfc,
  getSubRamo,
  getTipoFianza,
  toTimestamp,
  partitionTarifas,
} from '../utils/calculations';
import { useTarifas } from '../contexts/DataContext';
import { recordComisionEvento, isIgnorableLedgerError } from '../api/comisiones';
import { fianzaStoragePath } from '../utils/contratoStorage';

/**
 * UploadFianza Component
 * Handles uploading files for a fianza with drag-and-drop interface
 * Only XML Fianza is required - extracts structured data locally
 * Optional: Factura PDF, XML Factura, Fianza PDF
 * Saves file storage paths (not URLs) to database
 */
export function UploadFianza({ isOpen, onClose, clientId, clientName, clientRfc, linkId, contratoId, pendingFianzas = [], onSuccess, convenioId, convenioName, contratoProveedor }) {
  const [files, setFiles] = useState({
    facturaPdf: null,
    facturaXml: null,
    fianzaPdf: null,
    fianzaXml: null,
  });

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  // The fianza saved but its comisión did not book. Not an upload failure, so it
  // is surfaced as a warning rather than blocking — but never silently.
  const [ledgerWarning, setLedgerWarning] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [facturaData, setFacturaData] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);
  // Some fianza XMLs omit the vigencia dates — accept the XML and let the user
  // register them here instead of rejecting the document.
  const [needsDates, setNeedsDates] = useState(false);
  const [manualInicio, setManualInicio] = useState('');
  const [manualFin, setManualFin] = useState('');
  const [selectedFianzasRequeridas, setSelectedFianzasRequeridas] = useState([]);
  const [showFianzaSelection, setShowFianzaSelection] = useState(true);
  // Explicit "Otra" choice in the selection dialog: the fianza being uploaded
  // does not correspond to any requisito of this contract.
  const [otraFianza, setOtraFianza] = useState(false);
  const [renovable, setRenovable] = useState(false);
  // Normal fianzas: the beneficiario has a convenio de proveedores held by
  // another agent, so the org only keeps the fiado's share of the comisión.
  const [beneficiarioConvenio, setBeneficiarioConvenio] = useState(false);
  // Convenio fianzas: the fiado agent's %, when the owner already knows it.
  const [pctFiadoConocido, setPctFiadoConocido] = useState('');
  const [proveedorBannerDismissed, setProveedorBannerDismissed] = useState(false);
  const [validationBannerDismissed, setValidationBannerDismissed] = useState(false);
  // Styled pre-upload confirmation when there are validation warnings.
  const [showWarningsConfirm, setShowWarningsConfirm] = useState(false);
  // Post-save notify step: email the fiado contact + client accounts about
  // the fianza just uploaded (NotifyFianzaDialog loads the recipients).
  const [notifyData, setNotifyData] = useState(null); // {contratoId, fianzaId, numeroFianza}
  const { user, userData, isOwner } = useUser();
  const { tarifas } = useTarifas();

  // Hide body scroll when view is open and reset selection
  // Reset ONLY on the closed→open transition. Parents (ContratoView) rebuild
  // pendingFianzas inline on every render and keep a live onSnapshot on the
  // contract, so this effect re-runs mid-upload with a fresh array identity;
  // re-resetting then would yank the flow back to the selection dialog and
  // erase the user's "continuar sin seleccionar" choice. The ref guard makes
  // the reset once-per-open regardless of parent re-renders.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (!wasOpenRef.current) {
        setShowFianzaSelection(pendingFianzas.length > 0);
        setSelectedFianzasRequeridas([]);
        setOtraFianza(false);
        setRenovable(false);
        setBeneficiarioConvenio(false);
        setPctFiadoConocido('');
        setProveedorBannerDismissed(false);
        setValidationBannerDismissed(false);
      }
      wasOpenRef.current = true;
    } else {
      document.body.style.overflow = 'unset';
      wasOpenRef.current = false;
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, pendingFianzas]);

  // Handle file selection from FileReceiver
  const handleFileChange = async (fileType, droppedFiles) => {
    if (!droppedFiles || droppedFiles.length === 0) return;

    const file = droppedFiles[0];
    setFiles(prev => ({ ...prev, [fileType]: file }));
    setError(null);

    // If it's the fianza XML, parse it immediately
    if (fileType === 'fianzaXml' && file) {
      await handleParseXmlFianza(file);
    }

    // If it's the factura XML, parse it immediately
    if (fileType === 'facturaXml' && file) {
      await handleParseXmlFactura(file);
    }
  };

  // Handle file removal
  const handleRemoveFile = (fileType) => {
    setFiles(prev => ({ ...prev, [fileType]: null }));
    setError(null);

    // If it's the fianza XML, clear extracted data
    if (fileType === 'fianzaXml') {
      setExtractedData(null);
      setValidationWarnings([]);
      setNeedsDates(false);
      setManualInicio('');
      setManualFin('');
    }

    // If it's the factura XML, clear factura data
    if (fileType === 'facturaXml') {
      setFacturaData(null);
      setValidationWarnings([]);
    }
  };

  // Canonical fiado name from whichever XML(s) we have: the fianza fiado wins
  // when present, otherwise the factura receptor (since factura-only uploads
  // do happen and the receptor is the same legal entity in those CFDIs).
  const candidateFiadoName = (fianzaData, factura) =>
    fianzaData?.fiado?.nombre || factura?.receptor?.nombre || '';

  // Build a non-critical warning when the candidate fiado name diverges from
  // the contract's proveedor (only relevant when contratoProveedor is
  // provided, i.e. the convenio flow). Contract data prevails.
  const buildProveedorWarning = (candidateName, source) => {
    const mm = getProveedorMismatch(contratoProveedor, candidateName);
    if (!mm) return null;
    const sourceLabel = source === 'factura' ? 'receptor de la factura' : 'fiado del XML';
    return {
      type: 'warning',
      field: 'Proveedor del contrato',
      message: `El ${sourceLabel} ("${mm.fiadoXmlNombre}") no coincide con el proveedor del contrato ("${mm.contractProveedor}"). Se conservará el dato del contrato como fuente de verdad.`,
    };
  };

  // Cliente-path identity check (no convenio): the fiado of the fianza should
  // BE the cliente, so both the name and the RFC are compared against the
  // saved cliente record. In the convenio flow the fiado is the proveedor
  // (a different entity), so these checks don't apply there.
  const buildClienteWarnings = (fianzaData, factura) => {
    if (convenioId) return [];
    const warnings = [];
    const candidateName = candidateFiadoName(fianzaData, factura);
    if (clientName && candidateName && !namesMatch(clientName, candidateName)) {
      warnings.push({
        type: 'warning',
        field: 'Cliente',
        message: `El fiado ("${candidateName}") no coincide con el cliente ("${clientName}").`,
      });
    }
    const candidateRfc = fianzaData?.fiado?.rfc || factura?.receptor?.rfc || '';
    if (clientRfc && candidateRfc && !rfcsMatch(clientRfc, candidateRfc)) {
      warnings.push({
        type: 'warning',
        field: 'RFC del fiado',
        message: `El RFC del fiado ("${candidateRfc}") no coincide con el RFC del cliente ("${clientRfc}").`,
      });
    }
    return warnings;
  };

  // Parse XML Factura file using shared utility
  const handleParseXmlFactura = async (file) => {
    const result = await parseXmlFactura(file);

    if (!result.success) {
      setError(result.error);
      setFacturaData(null);
      setFiles(prev => ({ ...prev, facturaXml: null }));
      return;
    }

    setFacturaData(result.data);
    console.log('Datos extraidos de la factura:', result.data);

    const warnings = extractedData
      ? validateFianzaFactura(extractedData, result.data)
      : [];
    const candidate = candidateFiadoName(extractedData, result.data);
    const source = extractedData?.fiado?.nombre ? 'fianza' : 'factura';
    const pw = buildProveedorWarning(candidate, source);
    if (pw) warnings.push(pw);
    warnings.push(...buildClienteWarnings(extractedData, result.data));
    setValidationWarnings(warnings);
    setProveedorBannerDismissed(false);
    setValidationBannerDismissed(false);
  };



  // Parse XML Fianza file using shared utility
  const handleParseXmlFianza = async (file) => {
    const result = await parseXmlFianza(file);

    if (!result.success) {
      setError(result.error);
      setExtractedData(null);
      setFiles(prev => ({ ...prev, fianzaXml: null }));
      return;
    }

    setExtractedData(result.data);
    setError(null);
    // Prompt for vigencia dates when the XML didn't include them.
    setNeedsDates(Boolean(result.needsDates));
    if (!result.needsDates) { setManualInicio(''); setManualFin(''); }
    console.log('Datos extraidos del XML:', result.data);

    const warnings = facturaData
      ? validateFianzaFactura(result.data, facturaData)
      : [];
    const candidate = candidateFiadoName(result.data, facturaData);
    const pw = buildProveedorWarning(candidate, 'fianza');
    if (pw) warnings.push(pw);
    warnings.push(...buildClienteWarnings(result.data, facturaData));
    setValidationWarnings(warnings);
    setProveedorBannerDismissed(false);
    setValidationBannerDismissed(false);
  };

  // Upload files and save data
  const handleUpload = async () => {
    // Validate that XML Factura is present (XML Fianza is optional)
    if (!files.facturaXml) {
      setError('El XML de la Factura es obligatorio.');
      return;
    }

    if (!facturaData) {
      setError('No se pudo extraer la información del XML de la factura.');
      return;
    }

    // The fianza XML may omit vigencia dates; require the user's dates in that case.
    const dateInputToMs = (s) => (s ? new Date(`${s}T00:00:00`).getTime() : null);
    const resolvedInicio = extractedData?.inicioVigenciaFianza || dateInputToMs(manualInicio);
    const resolvedFin = extractedData?.finVigenciaFianza || dateInputToMs(manualFin);
    if (needsDates && (!resolvedInicio || !resolvedFin)) {
      setError('Registra las fechas de vigencia de la fianza (el XML no las incluye).');
      return;
    }

    // Warn about validation issues but allow proceed even on critical; the
    // user confirms once in a styled dialog instead of window.confirm.
    if (validationWarnings.length > 0) {
      setShowWarningsConfirm(true);
      return;
    }

    await performUpload();
  };

  // The actual upload, after validations passed and any warnings were confirmed.
  const performUpload = async () => {
    setShowWarningsConfirm(false);

    const dateInputToMs = (s) => (s ? new Date(`${s}T00:00:00`).getTime() : null);
    const resolvedInicio = extractedData?.inicioVigenciaFianza || dateInputToMs(manualInicio);
    const resolvedFin = extractedData?.finVigenciaFianza || dateInputToMs(manualFin);

    setUploading(true);
    setError(null);

    try {
      const timestamp = Date.now();
      const fianzaId = `fianza_${timestamp}`;
      const basePath = fianzaStoragePath({ id: contratoId, convenioId, linkId }, clientId, fianzaId);

      // Prepare upload promises and file paths
      const uploadPromises = [];
      const filePaths = {};

      // Upload files that are present
      if (files.facturaPdf) {
        const path = `${basePath}/factura.pdf`;
        uploadPromises.push(uploadBytes(ref(storage, path), files.facturaPdf));
        filePaths.facturaPdf = path;
      }

      if (files.facturaXml) {
        const path = `${basePath}/factura.xml`;
        uploadPromises.push(uploadBytes(ref(storage, path), files.facturaXml));
        filePaths.facturaXml = path;
      }

      if (files.fianzaPdf) {
        const path = `${basePath}/fianza.pdf`;
        uploadPromises.push(uploadBytes(ref(storage, path), files.fianzaPdf));
        filePaths.fianzaPdf = path;
      }

      // XML Fianza is optional
      if (files.fianzaXml) {
        const xmlPath = `${basePath}/fianza.xml`;
        uploadPromises.push(uploadBytes(ref(storage, xmlPath), files.fianzaXml));
        filePaths.fianzaXml = xmlPath;
      }

      // Upload all files in parallel
      await Promise.all(uploadPromises).catch(err => {
        throw new Error(`Error al subir archivos a Firebase Storage: ${err.message}`);
      });

      // Prepare fianza data object with storage paths (not URLs)
      const fianzaData = {
        id: fianzaId,
        contratoId,
        createdAt: timestamp,

        // File paths (storage locations without access tokens)
        filePaths,

        // Extracted XML data from fianza (can be null if not uploaded). When the
        // XML omitted vigencia, fold the user-registered dates back in so the
        // embedded payload stays consistent with the root fields.
        xmlFianza: extractedData
          ? { ...extractedData, inicioVigenciaFianza: resolvedInicio, finVigenciaFianza: resolvedFin }
          : null,

        // Extracted XML data from factura
        xmlFactura: facturaData,

        // Keep main fields at root level for easy access
        // Use extractedData if available, otherwise null (except monto from factura)
        numeroFianza: extractedData?.numeroFianza || facturaData.noIdentificacion || null,
        numeroInclusion: extractedData?.numeroInclusion || null,
        montoAfianzado: extractedData?.montoAfianzado || null,
        monto: facturaData.total, // Store the factura total as monto
        tipoFianza: extractedData?.tipo || null,
        inicioVigenciaFianza: resolvedInicio,
        finVigenciaFianza: resolvedFin,
        pagada: false,
        renovable,
        // Scenario marker: beneficiario with an external convenio de
        // proveedores — the ledger nets the org the fiado agent's share only.
        beneficiarioConvenio: Boolean(!convenioId && beneficiarioConvenio),

        // Lifecycle: fianza starts active. The event timeline is seeded with the
        // initial emisión so history is complete from creation (endosos append).
        estadoFianza: ESTADO_FIANZA.ACTIVA,
      };

      // Lock the comisión % at emisión so later facturas (aumento/extensión) earn
      // prima × this same %, and a $1 aumento is NOT re-tramoed (which would
      // falsely read as prima mínima). Null when no tarifa table matches yet —
      // reportes/ledger then derive it on the fly for this fianza.
      let comisionAviso = null;
      const emisionPrima = getPrimaAmount(fianzaData);
      const emisionMontoAfianzado = Number(fianzaData.montoAfianzado) || 0;
      const emisionTarifa = calculateTarifa(emisionPrima, emisionMontoAfianzado, {
        truncate: true,
        vigencia: {
          inicio: toTimestamp(fianzaData.inicioVigenciaFianza),
          fin: toTimestamp(fianzaData.finVigenciaFianza),
        },
      });
      if (convenioId) {
        // A convenio fianza's % belongs to the FIADO's agent — the org's own
        // tarifa tables never apply. Lock it only when the owner already knows
        // it; otherwise leave it null and the backend estimates from the
        // convenio-scope tables (pctEstimado) or books pct-pending.
        const conocido = isOwner ? parseFloat(pctFiadoConocido) : NaN;
        const capturado =
          Number.isFinite(conocido) && conocido >= 0 && conocido <= 100 ? conocido : null;
        fianzaData.comisionPct = capturado;
        // Provenance: a locked % alone cannot tell a person from a table, and
        // the convenio chip claims a PERSON confirmed the fiado agent's %.
        if (capturado != null) {
          fianzaData.comisionPctConfirmada = { by: user?.uid || null, at: Date.now() };
        }
      } else {
        const { comisionPct, pctEstimado } = calculateComision(
          emisionPrima,
          emisionTarifa,
          partitionTarifas(tarifas).propias,
          getEmisorNombre(fianzaData),
          getSubRamo(fianzaData),
          getTipoFianza(fianzaData),
          getEmisorRfc(fianzaData)
        );
        // A % from the Tabla general is an ESTIMATE — never lock it. The
        // backend re-derives it at emisión and books it pctEstimado, so the
        // fianza shows "por confirmar" until the owner confirms or creates
        // the afianzadora's own table (which offers the retarifa).
        fianzaData.comisionPct = pctEstimado ? null : comisionPct ?? null;
        const emisorNombre = getEmisorNombre(fianzaData) || 'esta afianzadora';
        if (pctEstimado) {
          comisionAviso =
            `Sin tabla de comisiones para ${emisorNombre}: la comisión se estimó ` +
            `con la Tabla general (${comisionPct}%, por confirmar). Crea su tabla ` +
            `en Configuración > Comisiones y podrás retarificar esta fianza.`;
        } else if (comisionPct == null) {
          comisionAviso =
            `Sin tabla de comisiones para ${emisorNombre}: la comisión quedó por ` +
            `confirmar. Créala en Configuración > Comisiones y podrás ` +
            `retarificar esta fianza.`;
        }
      }

      // Seed the emisión event and the factura ledger (facturas[0]) from the
      // just-built root fields.
      fianzaData.eventos = [createEmisionEvento(fianzaData)];
      fianzaData.facturas = [createEmisionFactura(fianzaData)];

      // Note: numeroFianza and montoAfianzado can be null when XML Fianza is not uploaded
      // These fields will be populated later when XML Fianza is added

      // Append the fianza and close out its fianzaRequerida in one transaction.
      // Appending by rewriting the whole array is a read-modify-write, so an
      // endoso landing on another fianza of the same contract at the same moment
      // would otherwise erase one of the two.
      await mutateContract(db, contratoId, (contractData) => {
        const updatePayload = {
          fianzas: [...(contractData.fianzas || []), fianzaData],
          updatedAt: timestamp,
        };

        if (selectedFianzasRequeridas.length > 0) {
          const fianzasRequeridas = contractData.fianzasRequeridas?.value || [];
          const selectedMatchers = selectedFianzasRequeridas.map((fr) => ({
            idx: fr.__index,
            tipo: fr.tipo,
            porcentaje: fr.porcentaje,
            monto: fr.monto,
          }));
          const updatedFianzasRequeridas = fianzasRequeridas.map((fr, idx) => {
            const matcher = selectedMatchers.find((m) => {
              if (typeof m.idx === 'number') return m.idx === idx;
              return (
                m.tipo === fr.tipo &&
                m.porcentaje === fr.porcentaje &&
                Math.abs((m.monto || 0) - (fr.monto || 0)) < 0.01
              );
            });
            if (!matcher) return fr;
            return {
              ...fr,
              estatus: 'emitida',
              fianzaId: fianzaId, // Link to the uploaded fianza
            };
          });

          updatePayload['fianzasRequeridas.value'] = updatedFianzasRequeridas;
          updatePayload['fianzasRequeridas.editedAt'] = timestamp;
        }

        return updatePayload;
      }).catch(err => {
        throw new Error(`Error al actualizar el contrato en Firestore: ${err.message}`);
      });

      // Book the emisión comisión movement in the ledger. The fianza already
      // exists on the contract, so a failure here does NOT roll the upload back —
      // but it must not pass silently either: a fianza whose emisión was never
      // booked earns no comisión in any report, forever, with nothing on screen
      // to say so. NO_TARIFA / EMISION_EXISTS are the expected "nothing to book"
      // cases and stay quiet.
      try {
        await recordComisionEvento({
          contractId: contratoId,
          fianzaId,
          eventoTipo: 'emision',
          eventoId: `evento_${timestamp}`,
          factura: fianzaData.facturas?.[0] || null,
          fecha: timestamp,
        });
      } catch (e) {
        if (!isIgnorableLedgerError(e)) {
          console.error('emisión ledger booking failed (fianza saved):', e);
          comisionAviso =
            `La fianza se guardó, pero no se registró su comisión: ${e.message} ` +
            'Vuelve a intentarlo desde el historial de la fianza o avisa a Surelia.';
        }
      }
      if (comisionAviso) setLedgerWarning(comisionAviso);

      // Log fianza upload to bitacora (non-blocking)
      const numeroFianza = extractedData?.numeroFianza ||
                           facturaData?.noIdentificacion ||
                           'sin número';

      try {
        const isConvenio = Boolean(convenioId);
        const metadata = isConvenio
          ? {
              convenioId,
              convenioName: convenioName || null,
              proveedor: contratoProveedor || null,
              contratoId,
              description: `Subió nueva fianza emitida #${numeroFianza}${contratoProveedor ? ` para proveedor '${contratoProveedor}'` : ''}`,
            }
          : {
              clientId,
              clientName: clientName || 'Sin nombre',
              contratoId,
              description: `Subió nueva fianza emitida #${numeroFianza} para cliente '${clientName || 'Sin nombre'}'`,
            };
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: contratoId,
          metadata,
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log fianza upload:', logError);
      }

      // Success!
      setUploading(false);

      // Call onSuccess callback if provided
      if (onSuccess) {
        await onSuccess();
      }

      // Notify step: fiado contact + client accounts with access to this
      // cliente, all preselected. Convenio fianzas have no cliente — skip
      // and close automatically, unless there is a comisión notice the user
      // must see (auto-closing used to hide the ledger warning entirely).
      if (!convenioId && clientId) {
        setNotifyData({ contratoId, fianzaId, numeroFianza, filePaths, eventos: fianzaData.eventos });
      } else if (!comisionAviso) {
        handleClose();
      }

    } catch (err) {
      console.error('Error al subir archivos:', err);
      setError(err.message || 'Error desconocido al subir la fianza');
      setUploading(false);
    }
  };

  // Reset and close
  const handleClose = () => {
    setFiles({
      facturaPdf: null,
      facturaXml: null,
      fianzaPdf: null,
      fianzaXml: null,
    });
    setExtractedData(null);
    setFacturaData(null);
    setValidationWarnings([]);
    setNeedsDates(false);
    setManualInicio('');
    setManualFin('');
    setError(null);
    setLedgerWarning(null);
    setUploading(false);
    setSelectedFianzasRequeridas([]);
    setShowFianzaSelection(true);
    setOtraFianza(false);
    setNotifyData(null);
    onClose();
  };

  // Close the notify step and the page with it. A ledger warning, if any,
  // is shown inside the notify dialog so it is read before closing.
  const finishNotify = () => {
    setNotifyData(null);
    handleClose();
  };

  if (!isOpen) return null;

  // Show fianza selection dialog if there are pending fianzas and none selected yet
  if (showFianzaSelection && pendingFianzas.length > 0) {
    return (
      <Dialog open={isOpen} onClose={handleClose} size="lg">
        <DialogTitle>Selecciona la Fianza que estás Subiendo</DialogTitle>
        <DialogBody>
          <Text className="text-sm text-gray-600 mb-4">
            Tienes {pendingFianzas.length} fianza{pendingFianzas.length > 1 ? 's' : ''} pendiente{pendingFianzas.length > 1 ? 's' : ''} en este contrato.
            Selecciona una o varias para actualizar su estado automáticamente, o marca "Otra" si no corresponde a ninguna.
          </Text>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {pendingFianzas.map((fianza, idx) => {
              const isChecked = selectedFianzasRequeridas.some(
                (sel) => sel.__index === fianza.__index
              );
              return (
                <label
                  key={idx}
                  className="w-full text-left border-2 border-gray-300 hover:border-azul-surelia hover:bg-azul-surelia/5 rounded-lg p-4 transition-all flex gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-azul-surelia focus:ring-azul-surelia"
                    checked={isChecked}
                    onChange={(e) => {
                      if (e.target.checked) setOtraFianza(false);
                      setSelectedFianzasRequeridas((prev) => {
                        if (e.target.checked) {
                          return [...prev, fianza];
                        }
                        return prev.filter((sel) => sel.__index !== fianza.__index);
                      });
                    }}
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-azul-surelia text-base mb-1">
                      {fianza.tipo}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                      <div>
                        <span className="text-gray-500">Monto:</span>{' '}
                        <span className="font-medium">
                          {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fianza.monto)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Porcentaje:</span>{' '}
                        <span className="font-medium">{fianza.porcentaje}%</span>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        fianza.pagada
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {fianza.estatus || 'pendiente'}
                      </span>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="mt-3">
            <label className="w-full text-left border-2 border-dashed border-gray-300 hover:border-azul-surelia hover:bg-azul-surelia/5 rounded-lg p-4 transition-all flex gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 text-azul-surelia focus:ring-azul-surelia"
                checked={otraFianza}
                onChange={(e) => {
                  setOtraFianza(e.target.checked);
                  if (e.target.checked) setSelectedFianzasRequeridas([]);
                }}
              />
              <div className="flex-1">
                <div className="font-semibold text-azul-surelia text-base mb-1">Otra</div>
                <div className="text-sm text-gray-600">
                  No corresponde a ninguna fianza requerida de este contrato.
                </div>
              </div>
            </label>
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <div className="flex justify-end gap-2">
              <Button color="white" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                color="surelia"
                onClick={() => setShowFianzaSelection(false)}
                disabled={selectedFianzasRequeridas.length === 0 && !otraFianza}
              >
                Continuar
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-[60] flex flex-col overflow-hidden">
      {/* Notify-by-email step, shown after a successful save */}
      <NotifyFianzaDialog
        open={!!notifyData}
        onClose={finishNotify}
        clientId={clientId}
        clientName={clientName}
        contratoId={notifyData?.contratoId}
        fianzaId={notifyData?.fianzaId}
        numeroFianza={notifyData?.numeroFianza}
        filePaths={notifyData?.filePaths}
        eventos={notifyData?.eventos}
        title="Notificar fianza por correo"
        warning={ledgerWarning}
      />

      {/* Pre-upload confirmation when the XMLs have validation issues */}
      <Dialog open={showWarningsConfirm} onClose={() => setShowWarningsConfirm(false)} size="lg">
        <DialogTitle>Revisa antes de subir</DialogTitle>
        <DialogBody>
          {(() => {
            const criticals = validationWarnings.filter((w) => w.type === 'critical');
            const warnings = validationWarnings.filter((w) => w.type !== 'critical');
            return (
              <div className="space-y-3">
                {criticals.length > 0 && (
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircleIcon className="w-5 h-5 text-red-600" />
                      <span className="font-semibold text-red-800">
                        {criticals.length} error{criticals.length !== 1 ? 'es' : ''} crítico{criticals.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <ul className="space-y-1.5 text-sm text-red-700">
                      {criticals.map((w, i) => (
                        <li key={i}>
                          <span className="font-semibold">{w.field}:</span> {w.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {warnings.length > 0 && (
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-2 mb-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">
                        {warnings.length} advertencia{warnings.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <ul className="space-y-1.5 text-sm text-amber-700">
                      {warnings.map((w, i) => (
                        <li key={i}>
                          <span className="font-semibold">{w.field}:</span> {w.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <Text className="text-sm text-zinc-600">
                  ¿Deseas continuar con la subida?
                </Text>
              </div>
            );
          })()}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setShowWarningsConfirm(false)}>
            Cancelar
          </Button>
          <Button color="amber" onClick={performUpload}>
            Continuar de todos modos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header with back button */}
      <div className="bg-azul-surelia text-white px-4 md:px-6 py-3 md:py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-2 md:gap-4 flex-1">
          <Button
            color="white"
            onClick={handleClose}
            className="flex items-center gap-1 md:gap-2 text-sm md:text-base"
            disabled={uploading}
          >
            <ArrowLeftIcon className="h-4 w-4 md:h-5 md:w-5" />
            <span className="hidden sm:inline">Volver</span>
          </Button>
          <div className="flex-1 min-w-0">
            <h2 className="text-base md:text-xl font-semibold truncate">Nueva Fianza</h2>
            {selectedFianzasRequeridas.length > 0 ? (
              <p className="text-xs md:text-sm opacity-90">
                {selectedFianzasRequeridas.length === 1
                  ? `${selectedFianzasRequeridas[0].tipo} - ${new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(selectedFianzasRequeridas[0].monto)}`
                  : `${selectedFianzasRequeridas.length} fianzas requeridas seleccionadas`}
              </p>
            ) : (
              <p className="text-xs md:text-sm opacity-90 hidden md:block">Arrastra los archivos XML requeridos</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            color="white"
            onClick={handleUpload}
          disabled={
            uploading ||
            !files.facturaXml ||
            !facturaData
          }
            className="text-xs md:text-base px-3 md:px-4 py-2"
          >
            <span className="hidden sm:inline">{uploading ? 'Subiendo...' : 'Subir Fianza'}</span>
            <span className="sm:hidden">{uploading ? 'Subiendo...' : 'Subir'}</span>
          </Button>
        </div>
      </div>

      {/* Top-level alert */}
      {error && (
        <div className="px-4 md:px-6 mt-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 shadow">
            <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
            <Text className="text-sm text-red-800">{error}</Text>
          </div>
        </div>
      )}

      {ledgerWarning && (
        <div className="px-4 md:px-6 mt-3">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 shadow">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <Text className="text-sm text-amber-800">{ledgerWarning}</Text>
          </div>
        </div>
      )}

      {/* Dedicated proveedor-mismatch banner — kept distinct from the generic
          validation summary so it stays visible even when other warnings exist.
          Uses the fianza fiado when available, falling back to the factura
          receptor for factura-only uploads. */}
      {(() => {
        if (proveedorBannerDismissed) return null;
        const candidate = extractedData?.fiado?.nombre || facturaData?.receptor?.nombre || '';
        const sourceLabel = extractedData?.fiado?.nombre
          ? 'Fiado en el XML de la fianza'
          : 'Receptor en el XML de la factura';
        if (!contratoProveedor || !candidate) return null;
        if (namesMatch(contratoProveedor, candidate)) return null;
        return (
          <div className="px-4 md:px-6 mt-3">
            <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 shadow flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <Text className="text-base font-bold text-amber-900">
                  El fiado del XML no coincide con el proveedor del contrato
                </Text>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="bg-white/70 rounded px-3 py-2 border border-amber-200">
                    <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Proveedor del contrato</div>
                    <div className="text-amber-900 font-medium truncate" title={contratoProveedor}>
                      {contratoProveedor}
                    </div>
                  </div>
                  <div className="bg-white/70 rounded px-3 py-2 border border-amber-200">
                    <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">{sourceLabel}</div>
                    <div className="text-amber-900 font-medium truncate" title={candidate}>
                      {candidate}
                    </div>
                  </div>
                </div>
                <Text className="text-xs text-amber-800 mt-2">
                  Puedes continuar con la subida — prevalecerá el dato del contrato como fuente de verdad.
                </Text>
              </div>
              <button
                type="button"
                onClick={() => setProveedorBannerDismissed(true)}
                aria-label="Cerrar aviso"
                className="text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded p-1 flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        );
      })()}

      {/* Top-level validation summary — surfaces XML mismatches so users don't miss them */}
      {!validationBannerDismissed && validationWarnings.length > 0 && (() => {
        const criticals = validationWarnings.filter((w) => w.type === 'critical');
        const warnings = validationWarnings.filter((w) => w.type !== 'critical');
        const hasCritical = criticals.length > 0;
        const tone = hasCritical
          ? { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', subtext: 'text-red-700', icon: 'text-red-600', closeHover: 'hover:bg-red-100', closeText: 'text-red-700 hover:text-red-900' }
          : { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-900', subtext: 'text-yellow-800', icon: 'text-yellow-600', closeHover: 'hover:bg-yellow-100', closeText: 'text-yellow-700 hover:text-yellow-900' };
        const items = [...criticals, ...warnings].slice(0, 4);
        const extra = validationWarnings.length - items.length;
        return (
          <div className="px-4 md:px-6 mt-3">
            <div className={`${tone.bg} border-2 ${tone.border} rounded-lg p-3 shadow flex items-start gap-3`}>
              <XCircleIcon className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} />
              <div className="min-w-0 flex-1">
                <Text className={`text-sm font-bold ${tone.text}`}>
                  {hasCritical
                    ? `${criticals.length} error${criticals.length !== 1 ? 'es' : ''} crítico${criticals.length !== 1 ? 's' : ''}`
                    : `${warnings.length} advertencia${warnings.length !== 1 ? 's' : ''}`}
                  {hasCritical && warnings.length > 0 && ` y ${warnings.length} advertencia${warnings.length !== 1 ? 's' : ''}`}
                  {' '}al validar los XMLs
                </Text>
                <ul className={`mt-1 space-y-0.5 text-xs ${tone.subtext}`}>
                  {items.map((w, i) => (
                    <li key={i} className="truncate">
                      <span className="font-semibold">{w.field}:</span> {w.message}
                    </li>
                  ))}
                  {extra > 0 && (
                    <li className="italic opacity-80">
                      +{extra} más — revisa el detalle abajo
                    </li>
                  )}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setValidationBannerDismissed(true)}
                aria-label="Cerrar aviso"
                className={`${tone.closeText} ${tone.closeHover} rounded p-1 flex-shrink-0`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        );
      })()}

      {/* Main content area — flex-1 lets it shrink/grow around the header
          and any alert/banner stack above, so the right column can always
          scroll to the bottom of the XML factura preview. */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0 gap-0">
        {/* Left column: File uploaders (full width on mobile, 1/3 on desktop) */}
        <div className="w-full md:w-1/3 md:border-r border-b md:border-b-0 border-gray-200 p-4 md:p-6 bg-gray-50 overflow-y-auto">
            {/* Compact requirements banner */}
            <div className="bg-azul-surelia/10 border border-azul-surelia/30 rounded-lg p-3 mb-4">
              <Text className="text-xs text-azul-surelia font-semibold">
                <strong>Requerido:</strong> XML Factura
              </Text>
              <Text className="text-xs text-zinc-600 mt-1">
                XML Fianza y PDFs opcionales
              </Text>
            </div>

            {/* Renovable checkbox */}
            <div
              className={`mb-4 p-3 bg-white border rounded-lg cursor-pointer transition-all hover:border-azul-surelia/50 ${
                renovable ? 'border-azul-surelia bg-azul-surelia/5' : 'border-gray-200'
              } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !uploading && setRenovable(!renovable)}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={renovable}
                  onChange={(e) => {
                    e.stopPropagation();
                    setRenovable(e.target.checked);
                  }}
                  disabled={uploading}
                  className="w-4 h-4 text-azul-surelia border-gray-300 rounded focus:ring-azul-surelia cursor-pointer"
                />
                <span className="text-sm text-gray-700">Fianza renovable</span>
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-7">
                Marcar si esta fianza puede ser renovada al vencer
              </p>
            </div>

            {/* Comisión split context */}
            {!convenioId ? (
              /* Normal fianza: mark when the beneficiario has an external
                 convenio de proveedores — the org keeps only the fiado share. */
              <div
                className={`mb-4 p-3 bg-white border rounded-lg cursor-pointer transition-all hover:border-azul-surelia/50 ${
                  beneficiarioConvenio ? 'border-azul-surelia bg-azul-surelia/5' : 'border-gray-200'
                } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !uploading && setBeneficiarioConvenio(!beneficiarioConvenio)}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={beneficiarioConvenio}
                    onChange={(e) => {
                      e.stopPropagation();
                      setBeneficiarioConvenio(e.target.checked);
                    }}
                    disabled={uploading}
                    className="w-4 h-4 text-azul-surelia border-gray-300 rounded focus:ring-azul-surelia cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">El beneficiario tiene convenio de proveedores</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-7">
                  La afianzadora reparte la comisión con el agente del convenio;
                  solo conservas la parte del agente del fiado
                </p>
              </div>
            ) : isOwner ? (
              /* Convenio fianza: the fiado agent's % is unknown until seen —
                 capture it here if already known. Owner-only: it is comisión
                 data, and correcting it afterwards goes through the owner-only
                 /override-pct. An operator uploads without it and the backend
                 estimates from the convenio tables or books pct-pending. */
              <div className={`mb-4 p-3 bg-white border border-gray-200 rounded-lg ${uploading ? 'opacity-50' : ''}`}>
                <label className="text-sm text-gray-700 block mb-1">
                  % de comisión del agente del fiado (si se conoce)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={pctFiadoConocido}
                  onChange={(e) => setPctFiadoConocido(e.target.value)}
                  disabled={uploading}
                  placeholder="Se estima con las tarifas de convenios si se deja vacío"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-azul-surelia focus:border-azul-surelia"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tu comisión será la participación del convenio sobre este
                  porcentaje. Puedes definirlo o corregirlo después desde la fianza.
                </p>
              </div>
            ) : null}

            {/* File upload stack */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
              {/* XML Fianza - OPTIONAL */}
              <FileReceiver
                title="XML Fianza (Opcional)"
                onFilesChange={(droppedFiles) => handleFileChange('fianzaXml', droppedFiles)}
                maxFiles={1}
                type="xml"
                disabled={uploading}
                currentFile={files.fianzaXml}
                onRemoveFile={() => handleRemoveFile('fianzaXml')}
                showFileName={true}
              />

              {/* Fianza PDF - Optional */}
              <FileReceiver
                title="Fianza PDF (Opcional)"
                onFilesChange={(droppedFiles) => handleFileChange('fianzaPdf', droppedFiles)}
                maxFiles={1}
                type="pdf"
                disabled={uploading}
                currentFile={files.fianzaPdf}
                onRemoveFile={() => handleRemoveFile('fianzaPdf')}
                showFileName={true}
              />

              {/* XML Factura - REQUIRED */}
              <div className="border-2 border-azul-surelia rounded-lg p-2 bg-white">
                <FileReceiver
                  title="XML Factura *"
                  onFilesChange={(droppedFiles) => handleFileChange('facturaXml', droppedFiles)}
                  maxFiles={1}
                  type="xml"
                  disabled={uploading}
                  currentFile={files.facturaXml}
                  onRemoveFile={() => handleRemoveFile('facturaXml')}
                  showFileName={true}
                />
              </div>

              {/* Factura PDF - Optional */}
              <FileReceiver
                title="Factura PDF (Opcional)"
                onFilesChange={(droppedFiles) => handleFileChange('facturaPdf', droppedFiles)}
                maxFiles={1}
                type="pdf"
                disabled={uploading}
                currentFile={files.facturaPdf}
                onRemoveFile={() => handleRemoveFile('facturaPdf')}
                showFileName={true}
              />
            </div>

          </div>

          {/* Right column: Extracted data (full width on mobile, 2/3 on desktop) */}
          <div className="w-full md:w-2/3 p-4 md:p-6 overflow-y-auto bg-white">
            {/* Empty state - show when no files uploaded */}
            {!extractedData && !facturaData && !error && (
              <div className="flex flex-col items-center justify-center h-full text-center py-12 md:py-20 px-4">
                <div className="mb-4 md:mb-6">
                  <svg className="w-16 h-16 md:w-24 md:h-24 mx-auto text-azul-surelia/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-2">
                  Sube el XML de Factura para comenzar
                </h3>
                <p className="text-gray-500 text-xs md:text-sm max-w-md">
                  Arrastra o selecciona el <strong>XML de Factura</strong> <span className="hidden md:inline">en el panel izquierdo</span><span className="md:hidden">arriba</span>.
                  La información se extraerá automáticamente y se mostrará aquí.
                </p>
                <div className="mt-4 md:mt-6 flex items-center gap-2 text-azul-surelia text-xs md:text-sm">
                  <CheckCircleIcon className="w-4 h-4 md:w-5 md:h-5" />
                  <span>XML Fianza y PDFs son opcionales</span>
                </div>
              </div>
            )}

            <div className="space-y-4">

          {/* Vigencia missing from the fianza XML — ask the user to register it */}
          {extractedData && needsDates && (
            <div className="rounded-lg border-2 border-yellow-300 bg-yellow-50 p-4 mt-4">
              <Heading level={4} className="text-sm text-yellow-800 mb-1 flex items-center gap-2 font-bold">
                <XCircleIcon className="w-5 h-5 text-yellow-600" />
                Vigencia no incluida en el XML
              </Heading>
              <Text className="text-xs text-yellow-800 mb-3">
                El XML de la fianza no trae las fechas de vigencia. Regístralas para continuar.
              </Text>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Inicio de vigencia</label>
                  <input type="date" value={manualInicio} onChange={(e) => setManualInicio(e.target.value)}
                    className="w-full rounded-lg border border-yellow-300 px-3 py-2 text-sm outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fin de vigencia</label>
                  <input type="date" value={manualFin} onChange={(e) => setManualFin(e.target.value)}
                    className="w-full rounded-lg border border-yellow-300 px-3 py-2 text-sm outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" />
                </div>
              </div>
            </div>
          )}

          {/* Validation warnings - FIRST */}
          {validationWarnings.length > 0 && (
            <div className="space-y-3 mt-4">
              {validationWarnings.map((warning, idx) => {
                const isCritical = warning.type === 'critical';
                const bgColor = isCritical ? 'bg-red-50' : 'bg-yellow-50';
                const borderColor = isCritical ? 'border-red-400' : 'border-yellow-300';
                const textColor = isCritical ? 'text-red-800' : 'text-yellow-800';
                const iconColor = isCritical ? 'text-red-600' : 'text-yellow-600';

                return (
                  <div key={idx} className={`${bgColor} border-2 ${borderColor} rounded-lg p-4`}>
                    <Heading level={4} className={`text-sm ${textColor} mb-2 flex items-center gap-2 font-bold`}>
                      <XCircleIcon className={`w-5 h-5 ${iconColor}`} />
                      {isCritical ? 'ERROR CRÍTICO' : 'Advertencia'}: {warning.field}
                    </Heading>
                    <Text className={`text-xs ${textColor}`}>
                      {warning.message}
                    </Text>
                  </div>
                );
              })}
            </div>
          )}

          {/* Extracted data preview - SECOND (Fianza) */}
          {extractedData && (
            <div className="bg-gradient-to-br from-green-50 to-azul-surelia/5 border-2 border-green-400/40 rounded-xl p-5 mt-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-green-400/30">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
                <Heading level={4} className="text-base font-bold text-green-700">
                  Datos Extraídos de la Fianza
                </Heading>
              </div>

              {/* Main Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Número de Fianza</span>
                  <Text className="text-zinc-800 font-bold">{extractedData.numeroFianza || 'N/A'}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">ID Póliza</span>
                  <Text className="text-zinc-800 font-bold">{extractedData.idPoliza || 'N/A'}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Folio</span>
                  <Text className="text-zinc-800 font-medium">{extractedData.folio || 'N/A'}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3 border-2 border-green-400">
                  <span className="font-semibold text-green-700 block mb-1">Monto Afianzado</span>
                  <Text className="text-zinc-900 font-bold text-sm">
                    {extractedData.montoAfianzado
                      ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.montoAfianzado)
                      : 'N/A'}
                  </Text>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Inicio Vigencia</span>
                  <Text className="text-zinc-800 font-medium">
                    {extractedData.inicioVigenciaFianza
                      ? new Date(extractedData.inicioVigenciaFianza).toLocaleDateString('es-MX')
                      : 'N/A'}
                  </Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Fin Vigencia</span>
                  <Text className="text-zinc-800 font-medium">
                    {extractedData.finVigenciaFianza
                      ? new Date(extractedData.finVigenciaFianza).toLocaleDateString('es-MX')
                      : 'N/A'}
                  </Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Fecha Solicitada</span>
                  <Text className="text-zinc-800 font-medium">
                    {extractedData.fechaSolicitada
                      ? new Date(extractedData.fechaSolicitada).toLocaleDateString('es-MX')
                      : 'N/A'}
                  </Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Fecha Autorización</span>
                  <Text className="text-zinc-800 font-medium">
                    {extractedData.fechaAutorizacion
                      ? new Date(extractedData.fechaAutorizacion).toLocaleDateString('es-MX')
                      : 'N/A'}
                  </Text>
                </div>
              </div>

              {/* Parties */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs mb-4">
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-2">Emisor</span>
                  <Text className="text-zinc-800 text-xs font-medium">{extractedData.emisor?.nombre || 'N/A'}</Text>
                  <Text className="text-green-600 text-xs mt-1">RFC: {extractedData.emisor?.rfc || 'N/A'}</Text>
                </div>
                {(() => {
                  const proveedorCardMismatch = contratoProveedor && extractedData.fiado?.nombre && !namesMatch(contratoProveedor, extractedData.fiado.nombre);
                  const clienteNameMismatch = !convenioId && clientName && extractedData.fiado?.nombre && !namesMatch(clientName, extractedData.fiado.nombre);
                  const clienteRfcMismatch = !convenioId && clientRfc && extractedData.fiado?.rfc && !rfcsMatch(clientRfc, extractedData.fiado.rfc);
                  return (
                    <div className={`bg-white/70 rounded-lg p-3 ${
                      proveedorCardMismatch || clienteNameMismatch || clienteRfcMismatch
                        ? 'border-2 border-amber-400'
                        : ''
                    }`}>
                      <span className="font-semibold text-green-700 block mb-2">Fiado</span>
                      <Text className="text-zinc-800 text-xs font-medium">{extractedData.fiado?.nombre || 'N/A'}</Text>
                      <Text className="text-green-600 text-xs mt-1">RFC: {extractedData.fiado?.rfc || 'N/A'}</Text>
                      {proveedorCardMismatch && (
                        <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-800 leading-snug">
                          <div className="font-semibold">No coincide con el proveedor del contrato</div>
                          <div className="text-amber-700 mt-0.5">
                            Contrato: <span className="font-medium">{contratoProveedor}</span>
                          </div>
                          <div className="text-amber-700 mt-0.5">
                            Prevalecerá el dato del contrato.
                          </div>
                        </div>
                      )}
                      {(clienteNameMismatch || clienteRfcMismatch) && (
                        <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-800 leading-snug">
                          <div className="font-semibold">No coincide con el cliente</div>
                          {clienteNameMismatch && (
                            <div className="text-amber-700 mt-0.5">
                              Cliente: <span className="font-medium">{clientName}</span>
                            </div>
                          )}
                          {clienteRfcMismatch && (
                            <div className="text-amber-700 mt-0.5">
                              RFC del cliente: <span className="font-medium">{clientRfc}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-2">Beneficiario</span>
                  <Text className="text-zinc-800 text-xs font-medium">{extractedData.beneficiario?.nombre || 'N/A'}</Text>
                </div>
              </div>

              {/* Classification & Amounts */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Ramo</span>
                  <Text className="text-zinc-800 font-medium">{extractedData.ramo || 'N/A'}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-green-700 block mb-1">Sub Ramo</span>
                  <Text className="text-zinc-800 font-medium">{extractedData.subRamo || 'N/A'}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3 col-span-2">
                  <span className="font-semibold text-green-700 block mb-1">Tipo</span>
                  <Text className="text-zinc-800 font-medium text-xs" title={extractedData.tipo}>
                    {extractedData.tipo || 'N/A'}
                  </Text>
                </div>
              </div>

              {/* Primas */}
              <div className="text-xs">
                <span className="font-semibold text-green-700 block mb-3">Primas y Costos</span>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2">
                    <Text className="text-zinc-700">Prima</Text>
                    <Text className="text-zinc-800 font-semibold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.prima || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2">
                    <Text className="text-zinc-700">Bonificación</Text>
                    <Text className="text-zinc-800 font-semibold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.bonificacion || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2">
                    <Text className="text-zinc-700">Derechos</Text>
                    <Text className="text-zinc-800 font-semibold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.derechos || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2">
                    <Text className="text-zinc-700">Gastos Expedición</Text>
                    <Text className="text-zinc-800 font-semibold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.gastosExpedicion || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2">
                    <Text className="text-zinc-700">Gastos Investigación</Text>
                    <Text className="text-zinc-800 font-semibold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.gastosInvestigacion || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2 mt-3">
                    <Text className="text-zinc-700">Sub Total</Text>
                    <Text className="text-zinc-800 font-semibold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.subTotal || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2">
                    <Text className="text-zinc-700">IVA</Text>
                    <Text className="text-zinc-800 font-semibold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.iva || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 border border-green-400 rounded-lg px-3 py-2">
                    <Text className="text-green-700 font-semibold">Prima Total</Text>
                    <Text className="text-zinc-900 font-bold text-sm">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(extractedData.primas?.primaTotal || 0)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2 mt-2">
                    <Text className="text-green-700 font-semibold">Tarifa</Text>
                    <Text className="text-zinc-800 font-bold">
                      {extractedData.tarifa != null ? `${extractedData.tarifa.toFixed(4)}%` : '0.0000%'}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Factura data preview - THIRD (Last) */}
          {facturaData && (
            <div className="bg-gradient-to-br from-azul-surelia/5 to-azul-surelia/10 border-2 border-azul-surelia/30 rounded-xl p-5 mt-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-azul-surelia/20">
                <CheckCircleIcon className="w-6 h-6 text-azul-surelia" />
                <Heading level={4} className="text-base font-bold text-azul-surelia">
                  Datos de la Factura (CFDI)
                </Heading>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-azul-surelia block mb-1">Serie-Folio</span>
                  <Text className="text-zinc-800 font-medium">{facturaData.serie}-{facturaData.folio}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-azul-surelia block mb-1">Fecha</span>
                  <Text className="text-zinc-800 font-medium">{new Date(facturaData.fecha).toLocaleDateString('es-MX')}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-azul-surelia block mb-1">No. Identificación</span>
                  <Text className="text-zinc-800 font-bold">{facturaData.noIdentificacion || 'N/A'}</Text>
                </div>
                <div className="bg-white/70 rounded-lg p-3 border-2 border-azul-surelia">
                  <span className="font-semibold text-azul-surelia block mb-1">Total Factura</span>
                  <Text className="text-zinc-900 font-bold text-sm">
                    {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(facturaData.total)}
                  </Text>
                </div>
              </div>

              {/* Folio Fiscal (UUID) */}
              {facturaData.folioFiscal && (
                <div className="bg-white/70 rounded-lg p-4 mb-4 text-xs">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="font-semibold text-azul-surelia block mb-2">Folio Fiscal (UUID)</span>
                      <Text className="text-zinc-800 font-mono text-xs break-all">{facturaData.folioFiscal}</Text>
                    </div>
                    {facturaData.fechaTimbrado && (
                      <div>
                        <span className="font-semibold text-azul-surelia block mb-2">Fecha Timbrado</span>
                        <Text className="text-zinc-800 text-xs font-medium">
                          {new Date(facturaData.fechaTimbrado).toLocaleString('es-MX')}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Emisor & Receptor */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mb-4">
                <div className="bg-white/70 rounded-lg p-3">
                  <span className="font-semibold text-azul-surelia block mb-2">Emisor</span>
                  <Text className="text-zinc-800 text-xs font-medium">{facturaData.emisor?.nombre}</Text>
                  <Text className="text-azul-surelia/70 text-xs mt-1">RFC: {facturaData.emisor?.rfc}</Text>
                </div>
                {(() => {
                  const proveedorCardMismatch = contratoProveedor && facturaData.receptor?.nombre && !namesMatch(contratoProveedor, facturaData.receptor.nombre);
                  const clienteNameMismatch = !convenioId && clientName && facturaData.receptor?.nombre && !namesMatch(clientName, facturaData.receptor.nombre);
                  const clienteRfcMismatch = !convenioId && clientRfc && facturaData.receptor?.rfc && !rfcsMatch(clientRfc, facturaData.receptor.rfc);
                  return (
                    <div className={`bg-white/70 rounded-lg p-3 ${
                      proveedorCardMismatch || clienteNameMismatch || clienteRfcMismatch
                        ? 'border-2 border-amber-400'
                        : ''
                    }`}>
                      <span className="font-semibold text-azul-surelia block mb-2">Receptor (Fiado)</span>
                      <Text className="text-zinc-800 text-xs font-medium">{facturaData.receptor?.nombre}</Text>
                      <Text className="text-azul-surelia/70 text-xs mt-1">RFC: {facturaData.receptor?.rfc}</Text>
                      {proveedorCardMismatch && (
                        <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-800 leading-snug">
                          <div className="font-semibold">No coincide con el proveedor del contrato</div>
                          <div className="text-amber-700 mt-0.5">
                            Contrato: <span className="font-medium">{contratoProveedor}</span>
                          </div>
                          <div className="text-amber-700 mt-0.5">
                            Prevalecerá el dato del contrato.
                          </div>
                        </div>
                      )}
                      {(clienteNameMismatch || clienteRfcMismatch) && (
                        <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-800 leading-snug">
                          <div className="font-semibold">No coincide con el cliente</div>
                          {clienteNameMismatch && (
                            <div className="text-amber-700 mt-0.5">
                              Cliente: <span className="font-medium">{clientName}</span>
                            </div>
                          )}
                          {clienteRfcMismatch && (
                            <div className="text-amber-700 mt-0.5">
                              RFC del cliente: <span className="font-medium">{clientRfc}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Conceptos breakdown */}
              <div className="text-xs">
                <span className="font-semibold text-azul-surelia block mb-3">Desglose de Conceptos</span>
                <div className="space-y-1.5">
                  {facturaData.conceptos?.map((concepto, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2 hover:bg-white/80 transition-colors">
                      <Text className="text-zinc-700">{concepto.descripcion}</Text>
                      <Text className="text-zinc-800 font-semibold">
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(concepto.importe)}
                      </Text>
                    </div>
                  ))}
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2 mt-3">
                    <Text className="text-azul-surelia font-semibold">SubTotal</Text>
                    <Text className="text-zinc-800 font-bold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(facturaData.subTotal)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 rounded-lg px-3 py-2">
                    <Text className="text-azul-surelia font-semibold">IVA ({facturaData.tasaIVA}%)</Text>
                    <Text className="text-zinc-800 font-bold">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(facturaData.totalImpuestos)}
                    </Text>
                  </div>
                  <div className="flex justify-between items-center bg-white/70 border border-azul-surelia rounded-lg px-3 py-2 mt-2">
                    <Text className="text-azul-surelia font-bold text-sm">Total</Text>
                    <Text className="text-zinc-900 font-bold text-sm">
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(facturaData.total)}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          )}

            </div>
          </div>
        </div>
      </div>
  );
}
